import { motion } from "framer-motion";
import { Formik, Form, getIn, useFormikContext, Field } from "formik";
import type { FormikHelpers } from "formik";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { rightModalVariants, useHandleLogout } from "./utils";
import { useAppDispatch, useAppSelector, useCustomTranslation, useFontSize, useIconSize, usePermissions } from "@app/hooks";
import {
	closeManageUsersModal,
	openUserModal,
} from "@app/features/header/headerSlice";
import {
	selectCurrentProject,
} from "@app/features/project/projectSlice";
import { selectCurrentUser } from "@app/features/auth/authSlice";
import { BiTrash } from "react-icons/bi";
import { FieldType } from "@typedefs/modulesInterfaces";
import { RiArrowRightFill } from "react-icons/ri";
import {
	useCreateProjectInvitationMutation,
	useGetGroupsQuery,
} from "@features/permissions/permissionsApiSlice";
import { useGetProjectMembershipsQuery } from "@features/project/projectApiSlice";
import type {
	InputGroup,
	RadioInputGroup,
} from "@typedefs/modulesInterfaces";
import type { ProjectInvitation } from "@app/features/permissions/permissionTypes";
import type { User } from "@typedefs/interfaces";
import Backdrop from "@components/modal/Backdrop";
import FormRow from "@components/input/formRow/FormRow";
import GenericInputGroup from "@components/input/inputGroup/GenericInputGroup";
import Button from "@components/button/Button";
import Spinner from "@components/spinner/Spinner";
import ErrorModal from "@components/modal/ErrorModal";
import usePreventCloseOnDragOutside from "./usePreventCloseOnDragOutside";
import ConfirmationModal from "@components/modal/ConfirmationModal";
import TranslatableText from "@components/translations/TranslatableText";
import useErrorMessage from "@utils/useErrorMessage";
import KebabMenu from "@components/kebabMenu/KebabMenu";
import useRemovememberFromProject from "./useRemoveMemberFromProject";
import * as Yup from "yup";
import { translate } from "@utils/helperFunctions";
import { EventTypes, useFirebaseHook } from "@utils/firebaseUtils";
import PublicProjectToggle from "./PublicProjectToggle";
import FormActionButtons from "@components/button/FormActionButtons";

interface FormValues {
	emails: string;
	group?: number;
}

const initialValues: FormValues = {
	emails: "",
	group: 0,
};

const groupRadio: RadioInputGroup = {
	type: FieldType.RADIO,
	inputName: "group",
	label: "",
	options: [
		{
			label: "main.analyst",
			value: 1,
		},
		{
			label: "main.second_reviewer",
			value: 2,
		},
		{
			label: "main.receiver",
			value: 3,
		},
	],
};

const validationSchema = Yup.object().shape({
	emails: Yup.string()
		.required("Emails are required")
		.test("is-valid-email", translate("validations.invalid_email_format"), (value) => {
			if (!value) return false;
			const emails = value.split(",").map(email => email.trim());
			return emails.every(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));// emails.every(email => /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(email));
		}),
	group: Yup.number()
		.required("validations.group_required")
		.min(1, "validations.select_valid_group"),
});

interface GroupOption {
	label: string;
	value: number;
}

const ModalContext = createContext<{ showDeleteModal: (memberId: number) => void } | null>(null);

const ManageUsersModal = ({ handleClose }: { handleClose: () => void }) => {
	const [inviteUser, { isLoading, isError, error, isSuccess }] =
		useCreateProjectInvitationMutation();
	const { errorMsg } = useErrorMessage({ isError, error })
	const { isFinalized } = usePermissions();
	const { project } = useAppSelector(selectCurrentProject);
	const { inputRef, handleMouseDownInside } = usePreventCloseOnDragOutside({
		isError,
		handleClose,
	});

	const { fireEvent } = useFirebaseHook();
	const { data: groups } = useGetGroupsQuery();
	const [groupOptions, setGroupOptions] = useState<GroupOption[]>([]);

	useEffect(() => {
		if (groups?.length) {
			const options = groups.map((group) => ({
				label: group.name,
				value: group.id,
			}));

			setGroupOptions(isFinalized ? options.filter((opt) => opt.label === "Admin") : options);
		}
	}, [groups, isFinalized]);

	const parseEmails = (input: string) => {
		const emailArray = input.split(",");
		const trimmedEmails = emailArray
			.map((email) => email.trim().toLowerCase())
			.filter((email) => email !== "");
		return trimmedEmails;
	};

	const handleSubmit = (
		values: FormValues,
		actions: FormikHelpers<FormValues>,
	) => {
		const parsedEmails = parseEmails(values.emails);
		for (const email of parsedEmails) {
			try {
				const payload: ProjectInvitation = {
					email,
					group: values.group ?? 0,
					project: project?.id ?? -1,
				};
				inviteUser(payload).unwrap();
				fireEvent(EventTypes.share_project);
			} catch (error) {
				console.error(error);
			}
		}
		actions.setSubmitting(false);
	};

	const { showDeleteModal, isDeleteDialogOpen, handleDelete, handleCancel } =
		useRemovememberFromProject();

	return (
		<ModalContext.Provider value={{ showDeleteModal }}>
			<Backdrop onClick={() => { }}>
				<motion.div
					onClick={(e) => e.stopPropagation()}
					variants={rightModalVariants}
					onMouseDown={handleMouseDownInside}
					className="right-side-modal align-items-start"
					initial="hidden"
					animate="visible"
					exit="exit"
				>
					<Header />
					{!project?.id ? (
						<div className="">
							<h3 className="pt-2">
								<TranslatableText translationKey="main.select_project_first" />
							</h3>
						</div>
					) : (
						<Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema}>
							<Form className="d-flex justify-content-center w-100 f-column gap-8">
								<h3 className="pt-2">
									<TranslatableText translationKey="main.invite_users" />
								</h3>
								<EmailsTextArea inputRef={inputRef} />
								<GenericInputGroup
									inputGroup={
										{ ...groupRadio, options: groupOptions ?? [] } as InputGroup
									}
								/>

								<UsersList />
								<Footer isLoading={isLoading} />
								<div className="pt-2">
									<PublicProjectToggle />
								</div>
							</Form>
						</Formik>
					)}
				</motion.div>

				{
					isError && <ManageUsersErrorModal isError={isError} errorMsg={errorMsg} />
				}
				{
					isSuccess && <InvitationSuccessModal isSuccess={isSuccess} />
				}

				<ConfirmationModal
					open={isDeleteDialogOpen}
					onCancel={handleCancel}
					onConfirm={handleDelete}
					message={<RemoveUserConfirmMessage />}
					headerText="user.user_delete_header"
					size="md"
				/>
			</Backdrop>
		</ModalContext.Provider>
	);
};

//TODO: normalize prop namings on modals
const ManageUsersErrorModal = ({
	isError,
	errorMsg
}: {
	isError: boolean;
	errorMsg?: string
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(isError);
	return (
		<ErrorModal
			isOpen={isOpen}
			handleClose={() => setIsOpen(false)}
			errorMessage={errorMsg ? errorMsg : "An error occured when inviting user"}
		/>
	);
};
const InvitationSuccessModal = ({
	isSuccess,
}: {
	isSuccess: boolean;
}) => {
	const [isOpen, setIsOpen] = useState<boolean>(isSuccess);
	return (
		<ConfirmationModal
			open={isOpen}
			onCancel={() => setIsOpen(false)}
			message="Invitation sent successfully"
			onConfirm={() => setIsOpen(false)}
		/>
	);
};
const Header = () => {
	const dispatch = useAppDispatch();
	const handleClick = () => {
		dispatch(closeManageUsersModal());
		dispatch(openUserModal());
	};
	const iconSize = useIconSize(24);
	const user = useAppSelector(selectCurrentUser);

	return (
		<header className="w-100 d-flex align-items-start">
			<div className="fs-18 d-flex f-column">
				<div className="d-flex">
					<TranslatableText translationKey="main.hello" />,
				</div>
				<span>
					{user?.first_name} {user?.last_name}
				</span>
				<Button
					onClick={handleClick}
					style={{
						height: 36,
						display: "flex",
						alignItems: "center",
						border: "1px solid var(--dark-gray)",
						borderRadius: "8px",
						marginTop: "16px",
					}}
					className="w-fit"
				>
					<RiArrowRightFill
						size={iconSize}
						color="#5D5555"
						style={{ padding: "0 0 0 8px", opacity: "0.75" }}
					/>
					<TranslatableText
						className="ps-1 fs-12"
						style={{ color: "#5D5555", opacity: "0.75", padding: "0 8px" }}
						translationKey="main.manage_your_account"
					/>
				</Button>
			</div>
		</header>
	);
};

const EmailsTextArea = ({
	inputRef,
}: { inputRef?: React.MutableRefObject<HTMLTextAreaElement | null> }) => {
	const { errors, touched, setFieldValue, values } = useFormikContext();
	const name = "emails";
	const isLoading = false;
	const formikVal = values as { [emails: string]: string };

	const showError = useMemo(
		() => !!(getIn(errors, name) && getIn(touched, name)),
		[errors, touched],
	);
	// const hasValue = useMemo(() => getIn(values, name) ? true : false, [values, name]);
	const [isFocused, setIsFocused] = useState(false);

	const handleFocus = () => setIsFocused(true);
	const handleBlur = () => setIsFocused(false);

	if (!formikVal.emails && inputRef) inputRef.current = null;
	const translatedLabel = useCustomTranslation("main.add_user_emails");

	const fontSize = useFontSize(14);

	return (
		<FormRow
			isFetching={isLoading}
			error={showError ? getIn(errors, name) : undefined}
			style={{ minHeight: "80px" }}
		>
			<Field
				label={translatedLabel}
				placeholder={translatedLabel}
				component="textarea"
				name={name}
				id={name}
				disabled={isLoading}
				setFieldValue={setFieldValue}
				className={`form-group ${showError ? "error" : isFocused ? "focused" : ""
					} ${isLoading ? "disabled" : ""}`}
				style={{ minHeight: "80px", color: "var(--darker-gray)", fontSize: fontSize }}
				onFocus={handleFocus}
				onBlur={handleBlur}
				rows={5}
				innerRef={formikVal.emails ? inputRef : undefined}
			/>
		</FormRow>
	);
};

type UserPlus = User & {
	membership: number;
};

interface GroupedUser {
	group?: { id: number; name: string };
	users: UserPlus[];
}

const UsersList = () => {
	const context = useContext(ModalContext);
	const { project } = useAppSelector(selectCurrentProject);
	const { data: projectUsers, isLoading, isError, error } = useGetProjectMembershipsQuery(project?.id ?? 0, {
		skip: !project?.id,
	});

	const { errorMsg } = useErrorMessage({ isError, error })
	const { isFinalized } = usePermissions();

	const [groupedUsers, setGroupedUsers] = useState<GroupedUser[]>([]);
	useEffect(() => {
		if (projectUsers?.length) {
			const grouped = projectUsers.reduce((acc: GroupedUser[], projUser) => {
				const group = acc.find((g) => g.group?.id === projUser.group?.id);
				if (group) group.users.push({ ...projUser.user, membership: projUser?.id });
				else
					acc.push({
						group: { id: projUser.group?.id, name: projUser.group?.name },
						users: [{ ...projUser.user, membership: projUser.id }],
					});
				return acc;
			}, []);
			setGroupedUsers(grouped);
		}
	}, [projectUsers]);

	return (
		<div className="w-100 d-flex f-column pt-2">
			{isError ?
				<span>{errorMsg}</span>
				: isLoading ?
					<Spinner size={20} isBlack />
					: groupedUsers ?
						groupedUsers.map((grouped, key) => (
							<div className="d-flex f-column pb-2 pt-1" key={grouped.group?.id}>
								<div
									className="w-100 pb-1 mb-1"
									style={{ borderBottom: "1px solid var(--gray)" }}
								>
									<h3 className="fs-18 ff-medium" style={{ color: "#3E4755" }}>
										{grouped.group?.name}
									</h3>
								</div>

								{grouped.users.map((user) => (
									<div className="ps-2 w-100 d-flex justify-content-between align-items-center" style={{ height: "24px" }} key={user.email}>
										<span
											className="fs-13 ff-medium text-darker-gray"
											style={{ color: "#5D5555" }}
										>
											{user.email}
										</span>
										{!isFinalized && (
											<KebabMenu
												lines={[{
													id: user.membership,
													label: "main.delete",
													icon: BiTrash,
													clickHandler: (id) => {
														if (id !== undefined && id !== null) context?.showDeleteModal(id)
														else console.warn("Attempted to delete a membership with undefined ID");
													},
												}]}
												activeColor="var(--error)"
												position="left"
											/>
										)}
									</div>
								))}
							</div>
						))
						: null
			}
		</div>
	);
};

const Footer = ({ isLoading }: { isLoading: boolean }) => {
    const handleLogout = useHandleLogout();
    const { isValid } = useFormikContext();
    return (
        <footer>
            <FormActionButtons
                isSubmitting={isLoading}
                isValid={isValid}
                onLogoutClick={handleLogout}
                submitBorderColor="#F06320"
                submitBgColor="#F06320"
                logoutBorderColor="#999999"
            />
        </footer>
    );
};

const RemoveUserConfirmMessage = () => {
	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
			<TranslatableText translationKey="user.user_remove_confirmation" />
		</div>
	)
}


export default ManageUsersModal;
